import { useFormik, FormikProvider } from 'formik'
import { Fragment, useState, useMemo, memo } from 'react'

import { ErrorMessage } from '@components/ErrorMessage'
import { Hexagon } from '@components/Hexagon'
import { CentraizedLoader } from '@components/Loader'
import { InputField } from '@fields/InputField'

import { config } from './config'
import { initialValues } from './constants'
import {
  Wrapper,
  Title,
  Container,
  Form,
  Label,
  Inputs,
  SoftPlatforms,
  SoftPlatform,
  HexagonWrapper,
  Content,
  SubmitButton,
  SmallHexagonWrapper,
  SubmittedText,
} from './styles'
import { SoftPlatformTypes } from './types'
import { validationSchema } from './validationSchema'

const {
  title,
  businessDomain,
  briefOverview: { textareaLabel, textarea },
  inputs,
  platforms,
  submitButton,
  platformsLabel,
  platformsError,
  submittedText,
} = config

export function CostCalculationComponent() {
  const [isSend, setIsSend] = useState(false)
  const [softPlatforms, setSoftPlatforms] = useState<string[]>([])

  const formik = useFormik({
    initialValues,
    onSubmit: async (data, actions) => {
      if (softPlatforms.length) {
        await fetch('/api/cost-calculation', {
          method: 'POST',
          body: JSON.stringify({
            ...data,
            platforms: softPlatforms.join(', '),
          }),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })

        actions.resetForm()
        setSoftPlatforms([])

        setIsSend(true)

        window.scrollTo({
          top: window.scrollY - 600,
          behavior: 'smooth',
        })

        setTimeout(() => {
          setIsSend(false)
        }, 6000)
      }
    },
    validationSchema,
  })

  const { handleSubmit, touched, isSubmitting } = formik

  const isTypeSelected = useMemo(
    () => (type: SoftPlatformTypes) =>
      softPlatforms.some(softPlatform => softPlatform === type),
    [softPlatforms]
  )

  const toggleSoftPlatformType = (selectedType: SoftPlatformTypes) => () => {
    if (isTypeSelected(selectedType)) {
      const newSoftPlatforms = softPlatforms.filter(
        softPlatform => softPlatform !== selectedType
      )

      setSoftPlatforms(newSoftPlatforms)
    } else {
      setSoftPlatforms(prevSoftPlatforms => [
        ...prevSoftPlatforms,
        selectedType,
      ])
    }
  }

  return isSend ? (
    <Wrapper>
      <Container>
        <SmallHexagonWrapper>
          <Hexagon icon="" />
        </SmallHexagonWrapper>
        <Content>
          <SubmittedText as="h3">{submittedText}</SubmittedText>
          <HexagonWrapper>
            <Hexagon icon="" />
          </HexagonWrapper>
        </Content>
      </Container>
    </Wrapper>
  ) : (
    <FormikProvider value={formik}>
      <Wrapper>
        <Container>
          <SmallHexagonWrapper>
            <Hexagon icon="" />
          </SmallHexagonWrapper>
          <Title as="h2">{title}</Title>
          <Form onSubmit={handleSubmit}>
            <InputField {...businessDomain} />
            <Label htmlFor={textarea.id}>{textareaLabel}</Label>
            <InputField as="textarea" {...textarea} />
            <Content>
              <Inputs>
                <Label>{platformsLabel}</Label>
                <SoftPlatforms>
                  {platforms.map(platform => (
                    <SoftPlatform
                      active={isTypeSelected(platform)}
                      key={platform}
                      onClick={toggleSoftPlatformType(platform)}
                    >
                      {platform}
                    </SoftPlatform>
                  ))}
                </SoftPlatforms>
                {!softPlatforms.length && touched.platforms && (
                  <ErrorMessage errorText={platformsError} />
                )}
                {inputs.map(({ id, label, input }) => (
                  <Fragment key={id}>
                    <Label htmlFor={input.id}>{label}</Label>
                    <InputField {...input} />
                  </Fragment>
                ))}
              </Inputs>
              <HexagonWrapper>
                <Hexagon icon="" />
              </HexagonWrapper>
            </Content>
            <SubmitButton
              id="cost-calculation-btn"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? <CentraizedLoader /> : submitButton}
            </SubmitButton>
          </Form>
        </Container>
      </Wrapper>
    </FormikProvider>
  )
}

export const CostCalculation = memo(CostCalculationComponent)
